/**
 * Query-key factory for the Buxgalteriya (Accounting) module.
 *
 * Prefix-stable layout: every key array starts with `'/v1/accounting'`
 * so `useEntityInvalidate('/v1/accounting')` invalidates the whole
 * sub-tree in one call (TanStack Query matches by key-array prefix).
 * Finer-grained prefixes — `'transactions'`, `'invoices'`, `'dashboard'` —
 * are exposed for callers that want to invalidate just one resource.
 *
 * Per-list keys include a `paramsSuffix` (the raw URL query string used to
 * fetch the page) and `lang` because backend-localised fields (currency
 * labels, category meta) must be refetched on a language switch — never
 * served from a previous-locale cache.
 *
 * Key prefixes are exported as plain strings — `useEntityInvalidate` accepts
 * `string | string[]`, so callers may pass either form. The structural keys
 * use `as const` so the array shape participates in TS narrowing.
 */

export const ACCOUNTING_PREFIX = '/v1/accounting'

export const accountingKeys = {
  all: [ACCOUNTING_PREFIX] as const,

  transactions: () => [ACCOUNTING_PREFIX, 'transactions'] as const,
  transactionsList: (paramsSuffix: string, lang: string) =>
    [ACCOUNTING_PREFIX, 'transactions', 'list', paramsSuffix, lang] as const,
  transactionDetail: (id: string) => [ACCOUNTING_PREFIX, 'transactions', 'detail', id] as const,

  invoices: () => [ACCOUNTING_PREFIX, 'invoices'] as const,
  invoicesList: (paramsSuffix: string, lang: string) =>
    [ACCOUNTING_PREFIX, 'invoices', 'list', paramsSuffix, lang] as const,
  invoiceDetail: (id: string) => [ACCOUNTING_PREFIX, 'invoices', 'detail', id] as const,

  dashboard: () => [ACCOUNTING_PREFIX, 'dashboard'] as const,
  dashboardSummary: (from: string, to: string, lang: string) =>
    [ACCOUNTING_PREFIX, 'dashboard', 'summary', from, to, lang] as const,

  // Single aggregate snapshot for the "To'lovga kutilmoqda" page. The
  // prefix-only `pendingPayments()` matches both per-lang variants in
  // `queryClient.getQueriesData` / `setQueriesData`, mirroring the
  // `transactions / transactionsList` convention above. `lang` participates
  // in the list key so locale switches refetch backend-localised labels.
  pendingPayments: () => [ACCOUNTING_PREFIX, 'pending-payments'] as const,
  pendingPaymentsList: (lang: string) =>
    [ACCOUNTING_PREFIX, 'pending-payments', 'list', lang] as const,
}
